import { AbsenceType, PolicyRuleType } from '@abs/shared-types';
import { resolveActiveTier } from './penaltyResolver';
import { classifyRisk } from './riskAssessor';
/**
 * Compute the effective (penalty-counting) absence breakdown from entries and policy.
 *
 * Steps:
 * 1. Count entries by type
 * 2. Apply partial absence values for late/early entries
 * 3. Apply tardy conversion (late + early → full absences)
 * 4. Apply grace allowance (free absences)
 * 5. Apply excused absence limit
 * 6. Sum total penalty absences
 */
export function computeEffectiveAbsences(entries, policy) {
    const unexcusedCount = entries.filter((e) => e.type === AbsenceType.Unexcused).length;
    const excusedCount = entries.filter((e) => e.type === AbsenceType.Excused).length;
    const lateCount = entries.filter((e) => e.type === AbsenceType.Late).length;
    const earlyCount = entries.filter((e) => e.type === AbsenceType.EarlyDeparture).length;
    // Partial absence value: configurable fraction per tardy/departure (default 1.0)
    // When tardy conversion is enabled, partialValue is applied before conversion
    const partialValue = policy.partialAbsenceValue ?? 1.0;
    // Tardy conversion
    let tardyConversions = 0;
    let remainingTardies = 0;
    if (policy.tardyConversion.enabled && policy.tardyConversion.tardiesPerAbsence > 0) {
        const totalTardies = lateCount + earlyCount;
        tardyConversions = Math.floor(totalTardies / policy.tardyConversion.tardiesPerAbsence);
        remainingTardies = totalTardies % policy.tardyConversion.tardiesPerAbsence;
    }
    // Grace allowance
    const effectiveUnexcused = Math.max(0, unexcusedCount - policy.grace.unexcusedGrace);
    const effectiveExcused = Math.max(0, excusedCount - policy.grace.excusedGrace);
    // Excused limit
    let countedExcused = effectiveExcused;
    if (policy.excusedAbsenceLimit !== null) {
        countedExcused = Math.min(effectiveExcused, policy.excusedAbsenceLimit);
    }
    // Total penalty absences:
    // - unexcused + excused + tardy/late contributions
    let totalPenaltyAbsences;
    if (!policy.tardyConversion.enabled && partialValue !== 1.0) {
        // Partial absences: late and early count as fractional
        totalPenaltyAbsences = effectiveUnexcused + countedExcused + (lateCount + earlyCount) * partialValue;
    }
    else if (policy.tardyConversion.enabled) {
        // Tardy conversion: whole absences from conversion only
        totalPenaltyAbsences = effectiveUnexcused + countedExcused + tardyConversions;
    }
    else {
        // Standard: late/early count as full absences (partialValue = 1.0)
        totalPenaltyAbsences = effectiveUnexcused + countedExcused + lateCount + earlyCount;
    }
    return {
        effectiveUnexcused,
        countedExcused,
        tardyConversions,
        totalPenaltyAbsences,
        remainingTardies,
    };
}
/**
 * Compute attendance percentage.
 * Returns 100.0 if there are 0 sessions (nothing to miss).
 * Clamps to [0, 100].
 */
export function computeAttendancePercentage(totalSessions, penaltyAbsenceCount) {
    if (totalSessions === 0)
        return 100.0;
    const attended = totalSessions - penaltyAbsenceCount;
    const clamped = Math.max(0, attended);
    return Math.min(100, (clamped / totalSessions) * 100);
}
/**
 * Compute the maximum allowed absences before auto-fail,
 * based on the policy rule type.
 */
export function computeMaxAbsences(policy, totalSessions) {
    if (policy.ruleType === PolicyRuleType.PercentageBased) {
        // attendanceFloor is the minimum attendance percentage
        const minAttended = Math.ceil((policy.attendanceFloor / 100) * totalSessions);
        return Math.max(0, totalSessions - minAttended);
    }
    // FixedCount or PerAbsenceDeduction — floor is expressed as count
    return policy.attendanceFloor;
}
/**
 * Compute remaining safe absences.
 */
export function computeRemainingBudget(policy, totalSessions, effectiveAbsences) {
    const maxAbsences = computeMaxAbsences(policy, totalSessions);
    return Math.max(0, maxAbsences - effectiveAbsences.totalPenaltyAbsences);
}
/**
 * Full budget computation — orchestrates all sub-calculations
 * and returns a complete BudgetResult.
 *
 * Credit-hour weighting: when creditHours !== 3.0, grade deductions
 * are multiplied by creditHours / 3.0 (normalized to standard 3-credit).
 */
export function computeFullBudget(_course, policy, entries, totalSessions) {
    const effectiveAbsences = computeEffectiveAbsences(entries, policy);
    const maxAbsences = computeMaxAbsences(policy, totalSessions);
    const remaining = Math.max(0, maxAbsences - effectiveAbsences.totalPenaltyAbsences);
    const attendancePercentage = computeAttendancePercentage(totalSessions, effectiveAbsences.totalPenaltyAbsences);
    const { activeTier, nextTier, absencesToNextTier } = resolveActiveTier(policy.tiers, effectiveAbsences.totalPenaltyAbsences);
    const riskLevel = classifyRisk(remaining, maxAbsences, attendancePercentage, policy.attendanceFloor, activeTier, nextTier, effectiveAbsences.totalPenaltyAbsences);
    return {
        totalAllowed: maxAbsences,
        usedUnexcused: effectiveAbsences.effectiveUnexcused,
        usedExcused: effectiveAbsences.countedExcused,
        usedTardyConversions: effectiveAbsences.tardyConversions,
        usedTotal: effectiveAbsences.totalPenaltyAbsences,
        remaining,
        attendancePercentage,
        activeTier,
        nextTier,
        absencesToNextTier,
        riskLevel,
    };
}
/**
 * Compute credit-hour-adjusted grade deduction.
 * Normalized to 3-credit standard — e.g., 4-credit course = 4/3 multiplier.
 */
export function computeCreditHourDeduction(baseDeduction, creditHours) {
    if (creditHours === 3.0 || creditHours <= 0)
        return baseDeduction;
    const multiplier = creditHours / 3.0;
    return baseDeduction * multiplier;
}
//# sourceMappingURL=budgetCalculator.js.map